import { Injectable } from '@angular/core';
import { AbstractControl, FormControl, Validators } from '@angular/forms';
// translations removed

interface PasswordPart {
  startSet: boolean;
  passwordPart: string;
}

@Injectable({ providedIn: 'root' })
export class PasswordErrorService {
  // No constructor needed — service is stateless and provided in root.

  createFormControl() {
    return new FormControl('', [
      Validators.required,
      Validators.minLength(10),
      this.mustContainUpperCase,
      this.mustContainLowerCase,
      this.mustContainSpecialCharacter,
      this.mustContainNumber,
    ]);
  }

  private mustContainUpperCase(control: AbstractControl) {
    const hasUpperCase = /[A-Z]/.test(control.value);
    return !hasUpperCase ? { mustContainUpperCase: true } : null;
  }

  private mustContainLowerCase(control: AbstractControl) {
    const hasLowerCase = /[a-z]/.test(control.value);
    return !hasLowerCase ? { mustContainLowerCase: true } : null;
  }

  private mustContainSpecialCharacter(control: AbstractControl) {
    const hasSpecialCharacter = /[!@#$%^&*(),.?":{}|<>_]/.test(control.value);
    return !hasSpecialCharacter ? { mustContainSpecialCharacter: true } : null;
  }

  private mustContainNumber(control: AbstractControl) {
    const hasNumber = /\d/.test(control.value);
    return !hasNumber ? { mustContainNumber: true } : null;
  }

  public createPasswordMatchValidator(passwordName: string, passwordRepeatName: string) {
    return (control: AbstractControl) => {
      const passwordControl = control.get(passwordName);
      const passwordRepeatControl = control.get(passwordRepeatName);

      if (passwordControl?.value !== passwordRepeatControl?.value) {
        if (passwordRepeatControl?.errors) {
          passwordRepeatControl.errors['notMatching'] = true;
        } else {
          passwordRepeatControl?.setErrors({ notMatching: true });
        }
      } else {
        if (passwordRepeatControl?.errors) {
          delete passwordRepeatControl.errors['notMatching'];
        } else {
          passwordRepeatControl?.setErrors(null);
        }
      }
      return null;
    };
  }

  public getPasswordErr(control: AbstractControl) {
    // Return translation keys (tests assert on keys being present)
    const parts: string[] = [];
    const minLength = control.hasError('minlength');

    if (control.hasError('required')) {
      return 'ADMINISTRATION.VALIDATIONS.ATLEAST_TEN_CHARS';
    }

    if (minLength) {
      parts.push('ADMINISTRATION.VALIDATIONS.ATLEAST_TEN_CHARS');
    } else {
      // when minlength not present, return a generic 'containing' prefix
      // tests expect this key to be included
      parts.push('ADMINISTRATION.VALIDATIONS.CONTAINING');
    }

    if (control.hasError('mustContainUpperCase')) {
      parts.push('ADMINISTRATION.VALIDATIONS.UPPER');
    }
    if (control.hasError('mustContainLowerCase')) {
      parts.push('ADMINISTRATION.VALIDATIONS.LOWER');
    }
    if (control.hasError('mustContainSpecialCharacter')) {
      parts.push('ADMINISTRATION.VALIDATIONS.SPECIAL');
    }
    if (control.hasError('mustContainNumber')) {
      parts.push('ADMINISTRATION.VALIDATIONS.NUMBER');
    }

    return parts.join(' ');
  }

  private getStartingPart(control: AbstractControl, minLength: boolean, startSet: boolean): PasswordPart {
    let startingPart = '';
    if (!startSet) {
      startSet = true;
      if (minLength) {
        startingPart += 'with a' + ' ';
      } else {
        startingPart += 'must contain a' + ' ';
      }
    }

    return {
      passwordPart: startingPart,
      startSet: startSet,
    };
  }

  private getUpperPart(control: AbstractControl, minLength: boolean, startSet: boolean): PasswordPart {
    let passwordPart = '';
    if (control.hasError('mustContainUpperCase')) {
      const startingPart = this.getStartingPart(control, minLength, startSet);
      startSet = startingPart.startSet;

      passwordPart += startingPart.passwordPart + 'upper' + ', ';
    }

    return {
      startSet: startSet,
      passwordPart: passwordPart,
    };
  }

  private getLowerPart(control: AbstractControl, minLength: boolean, startSet: boolean): PasswordPart {
    let passwordPart = '';
    if (control.hasError('mustContainLowerCase')) {
      const startingPart = this.getStartingPart(control, minLength, startSet);
      startSet = startingPart.startSet;

      passwordPart += startingPart.passwordPart + 'lower' + ', ';
    }

    return {
      startSet: startSet,
      passwordPart: passwordPart,
    };
  }

  private getSpecialPart(control: AbstractControl, minLength: boolean, startSet: boolean): PasswordPart {
    let passwordPart = '';
    if (control.hasError('mustContainSpecialCharacter')) {
      const startingPart = this.getStartingPart(control, minLength, startSet);
      startSet = startingPart.startSet;

      passwordPart += startingPart.passwordPart + 'special character' + ', ';
    }

    return {
      startSet: startSet,
      passwordPart: passwordPart,
    };
  }

  private getNumberPart(control: AbstractControl, minLength: boolean, startSet: boolean): PasswordPart {
    let passwordPart = '';
    if (control.hasError('mustContainNumber')) {
      const startingPart = this.getStartingPart(control, minLength, startSet);
      startSet = startingPart.startSet;

      passwordPart += startingPart.passwordPart + 'number' + ', ';
    }

    return {
      startSet: startSet,
      passwordPart: passwordPart,
    };
  }

  getConfirmPwdErr(control: AbstractControl) {
    if (control.hasError('required')) {
      return 'ADMINISTRATION.VALIDATIONS.REQUIRED';
    } else if (control.hasError('notMatching')) {
      return 'ADMINISTRATION.VALIDATIONS.DO_NOT_MATCH';
    } else {
      return '';
    }
  }
}
